import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCreateOrganizationsMutation } from "../../services/profileService";
import { styled } from "@mui/system";

// Validation schema using Yup
const validationSchema = yup.object().shape({
  organization_name: yup
    .string()
    .matches(
      /^[A-Za-z0-9 ]*$/,
      "Organization name cannot contain special characters"
    )
    .required("Organization name is required"),
  manager_name: yup.string().required("Manager name is required"),
  manager_email: yup
    .string()
    .email("Invalid email format")
    .required("Manager email is required"),
  manager_password: yup
    .string()
    .min(8, "Password should be at least 8 characters")
    .required("Manager password is required"),
});

const AddOrganization = () => {
  const navigate = useNavigate();
  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false);

  // React Hook Form initialization with validation
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const handleToastClose = () => {
    setToastOpen(false);
  };
  

  // Toast function for reusability
  const showToast = (message, severity) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const [createOrganization, { isLoading }] = useCreateOrganizationsMutation();

  const onSubmit = async (data) => {
    try {
      // Send the organization and manager details to the backend
      const {
        organization_name,
        manager_name,
        manager_email,
        manager_password,
      } = data;

      await createOrganization({
        organization_name,
        manager_name,
        manager_email,
        manager_password,
      }).unwrap();
      showToast("Organization and Manager created successfully!", "success");
      // setTimeout(() => navigate("/view-org"), 2000);
      setShowRegistrationDialog(true);
      // alert("Organization and Manager created successfully!");
      // navigate("/view-org");  // Redirect to the organizations list after successful creation
    } catch (error) {
      console.error("Error creating organization:", error);
      // alert("Manager with same email already exist in this organization");
      console.log(error);
      // alert(error?.message);
    }
  };
  const closeRegistrationDialog = () => {
    setShowRegistrationDialog(false);
  };
  

  return (
    <>
      <SectionWrap component="section">
        <ContainerWrap>
          <RowWrap>
            <Col12Wrap sx={{ textAlign: "center", marginBottom: "20px" }}>
              <Typography
                sx={{
                  fontSize: "25px",
                  textAlign: "center",
                  fontWeight: "500",
                  margin: "0 10px",
                  color: "#2C2C2E",
                }}
              >
                Add Organization
              </Typography>
            </Col12Wrap>
            <Col12Wrap>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormMiddleBoxWrap>
                  <RowWrap>
                    <Col6Wrap>
                      <FormGroupWrap>
                        <FormLabelWrap>Name</FormLabelWrap>
                        <CustomTextField
                          fullWidth
                          variant="outlined"
                          {...register("organization_name")}
                          placeholder="Organization Name"
                          error={!!errors.organization_name}
                        />
                        {errors.organization_name && (
                          <ErrorText>
                            {errors.organization_name.message}
                          </ErrorText>
                        )}
                      </FormGroupWrap>
                    </Col6Wrap>
                    <Col6Wrap>
                      <FormGroupWrap>
                        <FormLabelWrap>Owner</FormLabelWrap>
                        <CustomTextField
                          fullWidth
                          variant="outlined"
                          {...register("manager_name")}
                          placeholder="Owner Name"
                          error={!!errors.manager_name}
                        />
                        {errors.manager_name && (
                          <ErrorText>{errors.manager_name.message}</ErrorText>
                        )}
                      </FormGroupWrap>
                    </Col6Wrap>
                    <Col6Wrap>
                      <FormGroupWrap
                        sx={{
                          marginBottom: "0",
                          "@media (max-width: 767px)": { marginBottom: "30px" },
                        }}
                      >
                        <FormLabelWrap>Email</FormLabelWrap>
                        <CustomTextField
                          fullWidth
                          variant="outlined"
                          {...register("manager_email")}
                          placeholder="Owner Email"
                          error={!!errors.manager_email}
                        />
                        {errors.manager_email && (
                          <ErrorText>{errors.manager_email.message}</ErrorText>
                        )}
                      </FormGroupWrap>
                    </Col6Wrap>
                    <Col6Wrap>
                      <FormGroupWrap sx={{ marginBottom: "0" }}>
                        <FormLabelWrap>Password</FormLabelWrap>
                        <CustomTextField
                          fullWidth
                          variant="outlined"
                          type="password"
                          {...register("manager_password")}
                          placeholder="Owner Password"
                          error={!!errors.manager_password}
                        />
                        {errors.manager_password && (
                          <ErrorText>
                            {errors.manager_password.message}
                          </ErrorText>
                        )}
                      </FormGroupWrap>
                    </Col6Wrap>
                  </RowWrap>
                </FormMiddleBoxWrap>
                <FormBtnBoxWrap>
                  <RowWrap>
                    <Col12Wrap sx={{ textAlign: "center" }}>
                      <CustomBtn
                        type="submit"
                        variant="outlined"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Create Organization"
                        )}
                      </CustomBtn>
                      <CustomBtn
                        variant="contained"
                        color="secondary"
                        sx={{ marginLeft: "10px" }}
                        onClick={() => navigate("/view-org")}
                      >
                        Back to Admin Portal
                      </CustomBtn>
                    </Col12Wrap>
                  </RowWrap>
                </FormBtnBoxWrap>
              </form>
            </Col12Wrap>
          </RowWrap>
        </ContainerWrap>
      </SectionWrap>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000} // Close after 3s
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleToastClose}
          severity={toastSeverity}
          variant="filled"
        >
          {toastMessage}
        </Alert>
      </Snackbar>
      <Dialog
      open={showRegistrationDialog}
      onClose={closeRegistrationDialog}
      fullWidth
      maxWidth="sm"
      sx={{
        textAlign: "center",
        "& .MuiDialog-paper": {
          padding: "30px",
        },
      }}
    >
      <DialogTitle>REGISTRATION EMAIL HAS BEEN SENT</DialogTitle>
      <DialogActions sx={{ justifyContent: "center" }}>
        <CustomBtn onClick={closeRegistrationDialog}>OK</CustomBtn>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddOrganization;

const SectionWrap = styled(Box)(() => ({
  padding: "30px 0",
}));
const ContainerWrap = styled(Box)(() => ({
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
  marginLeft: "auto",
  marginRight: "auto",
}));
const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-15px",
  marginRight: "-15px",
}));
const Col12Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
}));
const FormMiddleBoxWrap = styled(Box)(() => ({
  padding: "50px 150px",
  background: "#ffffff",
  maxWidth: "1030px",
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: "16px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
    paddingLeft: "100px",
    paddingRight: "100px",
  },
  "@media (max-width: 991px)": {
    paddingLeft: "50px",
    paddingRight: "50px",
  },
  "@media (max-width: 575px)": {
    paddingLeft: "15px",
    paddingRight: "15px",
  },
}));
const Col6Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "50%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
  "@media (max-width: 767px)": {
    width: "100%",
  },
}));
const FormGroupWrap = styled(Box)(() => ({
  marginBottom: "30px",
  position: "relative",
}));
const CustomTextField = styled(TextField)(() => ({
  width: "99%",
  border: "1px solid #DADADA",
  borderRadius: "7px",
  "& .MuiInputBase-input": {
    fontSize: "16px",
    fontWeight: "300",
    lineHeight: "1",
    color: "#2C2C2E",
    padding: "14px 20px",
  },
  "& fieldset": {
    border: "none",
  },
}));
const ErrorText = styled(Typography)(() => ({
  fontSize: "12px",
  fontWeight: "400",
  lineHeight: "1.445",
  color: "#ff0000",
  marginTop: "5px",
}));
const FormLabelWrap = styled(Typography)(() => ({
  fontSize: "18px",
  fontWeight: "400",
  lineHeight: "1.445",
  color: "#2C2C2E",
  marginBottom: "5px",
}));
const FormBtnBoxWrap = styled(Box)(() => ({
  maxWidth: "1030px",
  marginLeft: "auto",
  marginRight: "auto",
  marginTop: "30px",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
  },
}));
const CustomBtn = styled(Button)(() => ({
  width: "100%",
  fontSize: "20px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "10px",
  padding: "15px 15px",
  maxWidth: "250px",
  boxShadow: "none !important",
}));
